import { copyToClipboard } from '@thoughtspot/browser-util';
import { getAccessToken, getCurrentOrgId, getScimBaseUrl, getUserPrivileges, isScimPerOrgEnabled, } from '@thoughtspot/session-service';
import { getIdpDomain } from '@thoughtspot/system-config-service';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCreateScimTokenMutation, useGetScimAuthTokenLazyQuery, } from '../../../../services/saml/saml-service';
import { TranslationsKeys } from '../../../authentication/constants';
import { useProvisioningMutation } from '../../../authentication/provisioning/useProvisioningCustomHook';
const SCIM_TOKEN_IDENTIFIER = 'primary';
const CLOSE_AFTER_SAVE_MS = 2000;
const buildScimHeaders = () => ({
    accessToken: `Bearer ${getAccessToken()}`,
    xTsPrivileges: btoa(JSON.stringify(getUserPrivileges())),
    xTsIdpDomain: getIdpDomain(),
});
export const useScimToken = ({ t }) => {
    const [nonce, setNonce] = useState('');
    const [tokenUrl, setTokenUrl] = useState('');
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [urlCopied, setUrlCopied] = useState(false);
    const [tokenCopied, setTokenCopied] = useState(false);
    const [showRegenerateWarning, setShowRegenerateWarning] = useState(false);
    const [showRegenerateError, setShowRegenerateError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [tokenFetchError, setTokenFetchError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [settingsChanged, setSettingsChanged] = useState(false);
    const scimBaseUrl = `${getScimBaseUrl()}/scim/v2/`;
    const { createScimToken, error: createError, data: createData, loading: createLoading, } = useCreateScimTokenMutation();
    const { fetchSignedToken, error: signedError, data: signedData, loading: signedLoading, } = useProvisioningMutation();
    const { getScimToken, error: getScimTokenError, ScimTokenAuthData, loading: getScimTokenLoading, } = useGetScimAuthTokenLazyQuery();
    const fetchSignedTokenRef = useRef(fetchSignedToken);
    fetchSignedTokenRef.current = fetchSignedToken;
    const tRef = useRef(t);
    tRef.current = t;
    const generateSignedToken = useCallback((nonceValue) => {
        fetchSignedTokenRef.current(window.location.hostname, nonceValue, `${getCurrentOrgId()}`, buildScimHeaders(), isScimPerOrgEnabled(), getScimBaseUrl());
    }, []);
    const initialFetchedRef = useRef(false);
    useEffect(() => {
        if (initialFetchedRef.current)
            return;
        initialFetchedRef.current = true;
        getScimToken(SCIM_TOKEN_IDENTIFIER);
    }, []);
    useEffect(() => {
        if (getScimTokenLoading)
            return;
        if (getScimTokenError) {
            setTokenFetchError(tRef.current(TranslationsKeys.PROVISIONING_FETCH_ERROR));
            return;
        }
        setTokenFetchError(null);
        if (ScimTokenAuthData?.token) {
            setShowRegenerateWarning(false);
            setNonce(ScimTokenAuthData.token);
        }
    }, [ScimTokenAuthData, getScimTokenError, getScimTokenLoading]);
    useEffect(() => {
        if (createLoading || (!createData && !createError))
            return;
        if (createError) {
            setIsLoading(false);
            setShowRegenerateError(true);
            setErrorMessage(createError.message ||
                tRef.current(TranslationsKeys.PROVISIONING_GENERATE_ERROR));
            setShowRegenerateWarning(false);
            return;
        }
        if (createData?.Error) {
            setIsLoading(false);
            setShowRegenerateError(true);
            setErrorMessage(createData.Error);
            setIsToastVisible(false);
            setShowRegenerateWarning(false);
            return;
        }
        const token = createData?.Data?.token;
        if (!token) {
            setIsLoading(false);
            setShowRegenerateError(true);
            setErrorMessage(tRef.current(TranslationsKeys.PROVISIONING_GENERATE_ERROR));
            setShowRegenerateWarning(false);
            return;
        }
        setShowRegenerateError(false);
        setTokenCopied(false);
        setIsToastVisible(true);
        setToastMessage(tRef.current(TranslationsKeys.PROVISIONING_GENERATE_SUCCESS));
        setNonce(token);
        generateSignedToken(token);
        setSettingsChanged(true);
    }, [createData, createError, createLoading, generateSignedToken]);
    useEffect(() => {
        if (signedLoading || (!signedData && !signedError))
            return;
        setIsLoading(false);
        if (signedError || !signedData?.token) {
            setShowRegenerateError(true);
            setErrorMessage(tRef.current(TranslationsKeys.PROVISIONING_GENERATE_ERROR));
            setShowRegenerateWarning(false);
            return;
        }
        setShowRegenerateError(false);
        setTokenUrl(signedData.token);
        setIsToastVisible(true);
        setToastMessage(tRef.current(TranslationsKeys.PROVISIONING_GENERATE_SUCCESS));
    }, [signedData, signedError, signedLoading]);
    const closeTimerRef = useRef(null);
    useEffect(() => () => {
        if (closeTimerRef.current)
            clearTimeout(closeTimerRef.current);
    }, []);
    const handleCopyUrl = useCallback(() => {
        copyToClipboard(scimBaseUrl, () => {
            setToastMessage(tRef.current(TranslationsKeys.SETTINGS_SCIM_BASEURL_COPIED_SUCCESS));
            setIsToastVisible(true);
            setUrlCopied(true);
        });
    }, [scimBaseUrl]);
    const handleCopyToken = useCallback(() => {
        copyToClipboard(tokenUrl, () => {
            setToastMessage(tRef.current(TranslationsKeys.PROVISIONING_COPIED_SUCCESS));
            setIsToastVisible(true);
            setTokenCopied(true);
        });
    }, [tokenUrl]);
    const isMutationInFlight = createLoading || signedLoading || isLoading;
    const createScimTokenRef = useRef(createScimToken);
    createScimTokenRef.current = createScimToken;
    const handleGenerateToken = useCallback(() => {
        if (isMutationInFlight)
            return;
        if (nonce && !showRegenerateWarning) {
            setShowRegenerateWarning(true);
            return;
        }
        setShowRegenerateError(false);
        setIsLoading(true);
        createScimTokenRef.current(SCIM_TOKEN_IDENTIFIER);
    }, [isMutationInFlight, nonce, showRegenerateWarning]);
    const closeWithSavedToast = useCallback((onClose) => {
        if (settingsChanged) {
            setToastMessage(tRef.current(TranslationsKeys.SETTINGS_AUTH_CHANGES_SAVED));
            setIsToastVisible(true);
            closeTimerRef.current = setTimeout(onClose, CLOSE_AFTER_SAVE_MS);
        }
        else {
            onClose();
        }
    }, [settingsChanged]);
    const notifySuccess = useCallback((message) => {
        setToastMessage(message);
        setIsToastVisible(true);
    }, []);
    return {
        scimBaseUrl,
        tokenUrl,
        urlCopied,
        tokenCopied,
        showRegenerateWarning,
        showRegenerateError,
        errorMessage,
        tokenFetchError,
        setShowRegenerateError,
        setShowRegenerateWarning,
        setTokenFetchError,
        isMutationInFlight,
        isInitialFetchLoading: getScimTokenLoading,
        toastMessage,
        isToastVisible,
        hideToast: () => setIsToastVisible(false),
        settingsChanged,
        markSettingsChanged: () => setSettingsChanged(true),
        handleCopyUrl,
        handleCopyToken,
        handleGenerateToken,
        closeWithSavedToast,
        notifySuccess,
    };
};
//# sourceMappingURL=use-scim-token.js.map